import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { app, db } from '../firebase';
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
  setPersistence,
  browserLocalPersistence,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
} from 'firebase/auth';

import { collection, collectionGroup, getDocs, query, where, writeBatch } from 'firebase/firestore';

import { mirrorAvatarFromUrl } from '../utils/uploads';

function stripCacheBust(url) {
  try {
    const u = new URL(url);
    u.searchParams.delete('v');
    return u.toString();
  } catch {
    return url;
  }
}

async function propagateAuthorIdentity({ uid, authorPhotoURL }) {
  if (!uid) return;
  if (!authorPhotoURL) return;

  const updates = [];
  const threadsQ = query(collection(db, 'threads'), where('authorId', '==', uid));
  const threadsSnap = await getDocs(threadsQ);
  threadsSnap.forEach((d) => {
    const data = { authorPhotoURL };
    updates.push({ ref: d.ref, data });
  });

  const commentsQ = query(collectionGroup(db, 'comments'), where('authorId', '==', uid));
  const commentsSnap = await getDocs(commentsQ);
  commentsSnap.forEach((d) => {
    const data = { authorPhotoURL };
    updates.push({ ref: d.ref, data });
  });

  for (let i = 0; i < updates.length; i += 400) {
    const batch = writeBatch(db);
    updates.slice(i, i + 400).forEach((u) => batch.update(u.ref, u.data));
    await batch.commit();
  }
}

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const auth = useMemo(() => getAuth(app), []);
  const googleProvider = useMemo(() => new GoogleAuthProvider(), []);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Ensure persistence is set per Firebase Web docs
    setPersistence(auth, browserLocalPersistence).catch(() => {});

    const syncGooglePhoto = async (signedInUser) => {
      try {
        if (!signedInUser) return;
        const google = (signedInUser.providerData || []).find((p) => p?.providerId === 'google.com');
        const latestGoogleUrl = google?.photoURL || signedInUser.photoURL || null;
        if (!latestGoogleUrl) return;

        // Optional: mirror the Google photo into R2 so we control hosting.
        const mirrorEnabled = String(process.env.REACT_APP_MIRROR_GOOGLE_AVATAR_TO_R2 || '').toLowerCase();
        const mirrorOn = mirrorEnabled === '1' || mirrorEnabled === 'true' || mirrorEnabled === 'yes';

        const uid = signedInUser.uid;
        // Avoid doing work every reload: only act when the Google photo changed.
        let lastGoogleUrl = '';
        try {
          if (typeof window !== 'undefined' && uid) {
            lastGoogleUrl = localStorage.getItem(`LAST_GOOGLE_PHOTO_URL_${uid}`) || '';
          }
        } catch {}

        const normalizedLatest = stripCacheBust(latestGoogleUrl);
        const normalizedLast = stripCacheBust(lastGoogleUrl);
        if (normalizedLast && normalizedLast === normalizedLatest) return;

        let updatedPhotoUrl = null;
        if (mirrorOn) {
          const mirrored = await mirrorAvatarFromUrl(latestGoogleUrl);
          const sep = mirrored.includes('?') ? '&' : '?';
          updatedPhotoUrl = `${mirrored}${sep}v=${Date.now()}`;
        } else {
          updatedPhotoUrl = latestGoogleUrl;
        }

        if (updatedPhotoUrl && signedInUser.photoURL !== updatedPhotoUrl) {
          await updateProfile(signedInUser, { photoURL: updatedPhotoUrl });
        }

        // Record the source Google URL we observed.
        try {
          if (typeof window !== 'undefined' && uid) {
            localStorage.setItem(`LAST_GOOGLE_PHOTO_URL_${uid}`, latestGoogleUrl);
          }
        } catch {}

        // Propagate to existing forum content (best-effort).
        try {
            if (uid && updatedPhotoUrl) {
            const propagatedKey = `LAST_PROPAGATED_PHOTO_URL_${uid}`;
            let lastProp = '';
            try {
              if (typeof window !== 'undefined') lastProp = localStorage.getItem(propagatedKey) || '';
            } catch {}

            if (stripCacheBust(lastProp) !== stripCacheBust(updatedPhotoUrl)) {
                await propagateAuthorIdentity({ uid, authorPhotoURL: updatedPhotoUrl });
              try {
                if (typeof window !== 'undefined') localStorage.setItem(propagatedKey, updatedPhotoUrl);
              } catch {}
            }
          }
        } catch (_) {
          // non-fatal
        }
      } catch (_) {
        // non-fatal
      }
    };

    // If Google sign-in used redirect flow, complete it here.
    // (onAuthStateChanged will still fire; this is mostly for surfacing errors.)
    getRedirectResult(auth)
      .then((cred) => syncGooglePhoto(cred?.user))
      .catch((e) => {
        // Non-fatal; user might not be coming from a redirect.
        if (e?.code) setError(e);
      });

    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
      // Best-effort: keep Google photo in sync on every session refresh.
      // (No-op for non-Google users.)
      syncGooglePhoto(u);
    });
    return () => unsub();
  }, [auth]);

  const signup = async (email, password, displayName) => {
    setError(null);
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    if (displayName) {
      try { await updateProfile(cred.user, { displayName }); } catch {}
    }
    return cred.user;
  };

  const login = async (email, password) => {
    setError(null);
    const cred = await signInWithEmailAndPassword(auth, email, password);
    return cred.user;
  };

  const loginWithGoogle = async () => {
    setError(null);
    try {
      const cred = await signInWithPopup(auth, googleProvider);
      // Ensure we capture the latest Google profile photo at login time.
      try {
        const google = (cred?.user?.providerData || []).find((p) => p?.providerId === 'google.com');
        const latestGoogleUrl = google?.photoURL || cred?.user?.photoURL || null;
        if (latestGoogleUrl) {
          const mirrorEnabled = String(process.env.REACT_APP_MIRROR_GOOGLE_AVATAR_TO_R2 || '').toLowerCase();
          const mirrorOn = mirrorEnabled === '1' || mirrorEnabled === 'true' || mirrorEnabled === 'yes';

          const uid = cred.user.uid;
          let updatedPhotoUrl = null;

          if (mirrorOn) {
            const mirrored = await mirrorAvatarFromUrl(latestGoogleUrl);
            const sep = mirrored.includes('?') ? '&' : '?';
            updatedPhotoUrl = `${mirrored}${sep}v=${Date.now()}`;
          } else {
            updatedPhotoUrl = latestGoogleUrl;
          }

          if (updatedPhotoUrl && cred.user.photoURL !== updatedPhotoUrl) {
            await updateProfile(cred.user, { photoURL: updatedPhotoUrl });
          }

          // Record the source url + propagate to content.
          try { if (typeof window !== 'undefined' && uid) localStorage.setItem(`LAST_GOOGLE_PHOTO_URL_${uid}`, latestGoogleUrl); } catch {}
          try {
            if (uid && updatedPhotoUrl) {
              const propagatedKey = `LAST_PROPAGATED_PHOTO_URL_${uid}`;
              let lastProp = '';
              try { if (typeof window !== 'undefined') lastProp = localStorage.getItem(propagatedKey) || ''; } catch {}
              if (stripCacheBust(lastProp) !== stripCacheBust(updatedPhotoUrl)) {
                await propagateAuthorIdentity({ uid, authorPhotoURL: updatedPhotoUrl });
                try { if (typeof window !== 'undefined') localStorage.setItem(propagatedKey, updatedPhotoUrl); } catch {}
              }
            }
          } catch (_) {}
        }
      } catch (_) {
        // non-fatal
      }
      return cred.user;
    } catch (e) {
      // Popup can be blocked on some browsers; fall back to redirect.
      const code = e?.code || '';
      if (
        code === 'auth/popup-blocked' ||
        code === 'auth/popup-closed-by-user' ||
        code === 'auth/cancelled-popup-request'
      ) {
        await signInWithRedirect(auth, googleProvider);
        return null;
      }
      throw e;
    }
  };

  const logout = async () => {
    setError(null);
    await signOut(auth);
  };

  const resetPassword = async (email) => {
    setError(null);
    await sendPasswordResetEmail(auth, email);
  };

  const refreshUser = async () => {
    try {
      if (auth.currentUser) {
        await auth.currentUser.reload();
        setUser(auth.currentUser);
      }
    } catch (e) {
      // non-fatal
    }
  };

  const value = { auth, user, loading, error, signup, login, loginWithGoogle, logout, resetPassword, refreshUser };
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};
